// ⚠️ GENERATED COPY — DO NOT EDIT.
// Source: shared/ui/journey/SignStep.tsx — edit there, then run: bash scripts/sync_shared_code.sh
// CI verifies these copies match the source (sync_shared_code.sh --check).
import React, { useRef, useState } from "react";
import { J } from "./journeyTheme";

/**
 * Typed-name signature with an attestation box. Ported from the approved
 * plan-holder prototype (Main.dc.html, sign screen); the page supplies the
 * heading. Controlled: the caller owns name, attested and submission.
 */

export interface SignStepProps {
  name: string;
  onNameChange: (v: string) => void;
  attested: boolean;
  onAttestedChange: (v: boolean) => void;
  statement: string;
  onSubmit: () => void;
  submitting?: boolean;
  summary?: string;
  onReview?: () => void;
  submitLabel?: string;
  disabledLabel?: string;
}

const RAISED_SHADOW = "0 1px 2px rgba(24,42,51,.06), 0 8px 22px rgba(24,42,51,.07)";

// React 17 has no useId; a module counter gives each mount stable label ids.
let idSeq = 0;

const Check = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12l5 5L20 7" />
  </svg>
);

export default function SignStep({
  name,
  onNameChange,
  attested,
  onAttestedChange,
  statement,
  onSubmit,
  submitting = false,
  summary,
  onReview,
  submitLabel,
  disabledLabel,
}: SignStepProps) {
  const idRef = useRef<string>("");
  if (!idRef.current) idRef.current = `pj-sign-${++idSeq}`;
  const nameId = `${idRef.current}-name`;
  const attestId = `${idRef.current}-attest`;
  const [focused, setFocused] = useState(false);

  // One character is not a name; the prototype's threshold.
  const ready = name.trim().length > 1 && attested;
  const canSubmit = ready && !submitting;
  const label = ready ? submitLabel ?? "Sign and submit" : disabledLabel ?? "Sign to enable submit";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, flex: "1 1 0" }}>
      {summary ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: J.tint,
            borderRadius: 12,
            padding: "10px 12px",
          }}
        >
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: J.sage,
              color: J.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "0 0 22px",
            }}
          >
            <Check size={12} />
          </div>
          <div style={{ fontSize: 13.5, color: "#1b4a40", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
            <strong>{summary}</strong>
            {onReview ? (
              <button
                type="button"
                onClick={onReview}
                style={{
                  // Reads as the prototype's inline link, but keeps a 44px
                  // touch target via vertical padding and negative margin.
                  background: "transparent",
                  border: 0,
                  padding: "13px 4px",
                  margin: "-13px 0",
                  color: J.teal,
                  textDecoration: "underline",
                  fontSize: 13.5,
                  fontFamily: "inherit",
                  cursor: "pointer",
                }}
              >
                Review
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div
        style={{
          background: J.white,
          borderRadius: 18,
          padding: 18,
          boxShadow: RAISED_SHADOW,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <label htmlFor={nameId} style={{ fontSize: 13, color: J.muted }}>
          Type your full name
        </label>
        <input
          id={nameId}
          type="text"
          autoComplete="name"
          placeholder="Your full name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            height: 50,
            border: `1.5px solid ${focused ? J.teal : J.border}`,
            borderRadius: 12,
            padding: "0 14px",
            fontSize: 17,
            fontFamily: "inherit",
            color: J.ink,
            background: J.white,
            outline: "none",
            width: "100%",
            boxSizing: "border-box",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            height: 84,
            borderRadius: 12,
            background: "#faf7f2",
            border: `1px dashed ${J.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: '"Dancing Script", cursive',
            fontSize: 32,
            color: J.ink,
            opacity: name ? 1 : 0.35,
            transition: "opacity .25s ease",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            padding: "0 12px",
          }}
        >
          {name || "Your signature"}
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, paddingTop: 4 }}>
          <input
            id={attestId}
            type="checkbox"
            checked={attested}
            onChange={(e) => onAttestedChange(e.target.checked)}
            style={{ width: 20, height: 20, margin: "2px 0 0", accentColor: J.teal, flex: "0 0 20px" }}
          />
          <label htmlFor={attestId} style={{ fontSize: 13.5, lineHeight: 1.45, color: J.body }}>
            {statement}
          </label>
        </div>
      </div>

      <div style={{ marginTop: "auto", paddingTop: 10 }}>
        <button
          type="button"
          onClick={() => {
            if (canSubmit) onSubmit();
          }}
          disabled={!canSubmit}
          aria-busy={submitting || undefined}
          style={{
            width: "100%",
            height: 52,
            border: 0,
            borderRadius: J.radiusButton,
            background: ready ? J.ink : J.disabled,
            color: J.white,
            fontSize: 16,
            fontWeight: 600,
            fontFamily: "inherit",
            cursor: canSubmit ? "pointer" : "default",
            opacity: submitting ? 0.6 : 1,
            transition: "background .25s ease, opacity .25s ease",
          }}
        >
          {label}
        </button>
      </div>
    </div>
  );
}
